import { BaseModelWithFiles } from "@/models/base-with-files.model"
import { type Moment } from "moment"
import { type IRentalMutation } from "@/interfaces/api/rental-mutation.interface"
import { RentalMutationFlowDirections } from "@/enums/rental-mutation-flow-directions"
import { ShallowRentalMutation } from "@/models/shallow-rental-mutation"
import { Project } from "@/models/project.model"
import { ModelFactory } from "@/factories/model-factory"

export abstract class RentalMutation extends BaseModelWithFiles implements IRentalMutation {
  public static className = "RentalMutation"
  public static verboseName = "Huurmutatie"

  public static override managedResolveFields = ["project"]

  id: string

  instruction: string

  project?: Project

  counterMutation?: RentalMutation // To be concretely implemented in RentalIntake and RentalDispense

  counteredBy: ShallowRentalMutation[] // To be concretely implemented in RentalIntake and RentalDispense

  flowDirection: number

  quantity: string | undefined

  commencementDate: Moment

  adminComment: string

  sortIndex: number

  materializationCount: number

  // Annotated
  instructionReference: string
  orderReference: string
  instructionDate?: Moment

  protected constructor(rentalMutation: IRentalMutation, managed = false) {
    super(rentalMutation, managed)

    this.project = ModelFactory.childModel(Project, rentalMutation.project, managed)

    this.id = rentalMutation.id
    this.instruction = rentalMutation.instruction
    this.flowDirection = rentalMutation.flowDirection
    this.quantity = rentalMutation.quantity
    this.commencementDate = rentalMutation.commencementDate
    this.adminComment = rentalMutation.adminComment
    this.sortIndex = rentalMutation.sortIndex

    this.materializationCount = rentalMutation.materializationCount
    this.instructionReference = rentalMutation.instructionReference
    this.instructionDate = rentalMutation.instructionDate
    this.orderReference = rentalMutation.orderReference

    this.counteredBy = []
  }

  public getProject() {
    return this.flowDirection === RentalMutationFlowDirections.ISSUANCE
      ? this.project
      : this.counterMutation?.project
  }

  public getFlowDirectionText(): string {
    return this.flowDirection === RentalMutationFlowDirections.ISSUANCE ? "Uitgifte" : "Retour"
  }

  public isIssuance(): boolean {
    return this.flowDirection === RentalMutationFlowDirections.ISSUANCE
  }

  public isReturn(): boolean {
    return this.flowDirection === RentalMutationFlowDirections.RETURN
  }

  public isComplete(): boolean {
    return (
      (!!this.project || this.flowDirection === RentalMutationFlowDirections.RETURN) &&
      !!this.commencementDate &&
      !!this.quantity &&
      this.getQuantity() !== 0
    )
  }

  public getCounterQuantity(): number {
    if (!this.isIssuance()) {
      throw new Error(
        "getCounterQuantity() should only be called on RentalIntake with flowDirection ISSUANCE."
      )
    }
    let counterQuantity = 0
    for (const counteredBy of this.counteredBy) {
      counterQuantity += Number(counteredBy.quantity)
    }

    return counterQuantity
  }

  public getRemainingQuantity(): number {
    return this.getQuantity() - this.getCounterQuantity()
  }

  public getQuantity(): number {
    return parseFloat(this.quantity ?? "0")
  }
}
